import { useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import PageMeta from '@/components/base/PageMeta';
import Breadcrumb from '@/components/base/Breadcrumb';
import EmptyState from '@/components/base/EmptyState';
import { books, siteConfig } from '@/mocks/data';

export default function BookDetail() {
  const { slug } = useParams<{ slug: string }>();
  const book = books.find((b) => b.slug === slug);

  if (!book) {
    return (
      <div className="min-h-screen bg-background-50">
        <PageMeta
          title="找不到書籍｜樂律 Lelyric"
          description="找不到指定的書籍頁面，請返回書籍列表或前往崧博出版書籍資料庫查詢。"
          canonicalPath={`/books/${slug}`}
          noindex
        />
        <Navbar />
        <main>
          <section className="relative min-h-[300px] md:min-h-[420px] flex items-center bg-foreground-950 overflow-hidden">
            <div className="absolute inset-0">
              <img
                src="https://readdy.ai/api/search-image?query=Abstract%20artistic%20composition%20inspired%20by%20the%20quiet%20rhythm%20of%20bookshelves%20and%20stacked%20volumes%2C%20overlapping%20rectangular%20forms%20and%20layered%20translucent%20shapes%20in%20warm%20cream%20ivory%20and%20deep%20muted%20indigo%20tones%2C%20soft%20ambient%20light%20creating%20gentle%20diffuse%20shadows%20across%20the%20scene%2C%20editorial%20minimalism%20with%20subtle%20paper%20textures%20and%20fine%20grain%2C%20serene%20contemplative%20mood%2C%20no%20people%2C%20no%20text%2C%20painterly%20modern%20digital%20art%20aesthetic%2C%20dark%20navy%20and%20warm%20neutral%20palette%2C%20publishing%20house%20brand%20visual%20identity%20with%20understated%20elegance%20and%20generous%20breathing%20negative%20space&width=1600&height=800&seq=lelyric-books-hero-bg&orientation=landscape"
                alt=""
                width={1600}
                height={800}
                className="w-full h-full object-cover"
              />
            </div>
            <div className="absolute inset-0 bg-gradient-to-b from-foreground-950/75 via-foreground-950/60 to-foreground-950/80"></div>
            <div className="absolute top-0 left-0 right-0 h-px bg-background-50/6"></div>
            <div className="absolute bottom-0 left-0 right-0 h-px bg-background-50/6"></div>
            <div className="relative z-10 w-full px-6 md:px-10 py-16 md:py-20">
              <div className="max-w-4xl mx-auto">
                <Breadcrumb
                  items={[
                    { label: '首頁', href: '/' },
                    { label: '書籍', href: '/books' },
                    { label: '找不到書籍' },
                  ]}
                  light
                />
                <h1 className="font-heading text-[3rem] md:text-[4rem] font-bold text-background-50 leading-[1.12] tracking-tight mb-4">
                  找不到書籍
                </h1>
                <p className="text-base md:text-lg text-background-50/50 max-w-xl leading-relaxed font-light tracking-wide">
                  找不到指定的書籍頁面，可能已被移除或連結無效。請返回書籍列表，或前往崧博出版書籍資料庫查詢完整書目。
                </p>
              </div>
            </div>
            <div className="absolute bottom-0 left-0 right-0 h-20 bg-gradient-to-t from-background-50/20 to-transparent"></div>
          </section>

          <section className="px-6 md:px-10 py-16 md:py-24 bg-background-50">
            <div className="max-w-3xl mx-auto">
              <EmptyState
                icon="ri-book-2-line"
                title="找不到書籍"
                description="請返回書籍列表，或前往書籍資料庫查詢。"
                actions={[
                  { label: '返回書籍列表', path: '/books', primary: true },
                  { label: '前往書籍資料庫', path: siteConfig.catalogUrl, external: true },
                ]}
              />
            </div>
          </section>
        </main>
        <Footer />
      </div>
    );
  }

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首頁', item: 'https://lelyric.sonbook.net/' },
      { '@type': 'ListItem', position: 2, name: '書籍', item: 'https://lelyric.sonbook.net/books' },
      { '@type': 'ListItem', position: 3, name: book.title, item: `https://lelyric.sonbook.net/books/${book.slug}` },
    ],
  };

  return (
    <div className="min-h-screen bg-background-50">
      <PageMeta
        title={`${book.title}｜樂律 Lelyric`}
        description={book.description || `查看 ${book.title} 的書籍資訊。`}
        canonicalPath={`/books/${book.slug}`}
        structuredData={breadcrumbSchema}
      />
      <Navbar />
      <main>
        {/* Hero */}
        <section className="relative min-h-[280px] md:min-h-[380px] flex items-center bg-foreground-950 overflow-hidden">
          {/* Full-bleed background image */}
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=Abstract%20artistic%20composition%20with%20gentle%20flowing%20paper-like%20curves%20and%20subtle%20layered%20geometric%20forms%2C%20warm%20ivory%20cream%20blending%20into%20deep%20muted%20charcoal%20and%20indigo%20shadows%2C%20soft%20diffused%20lighting%20from%20the%20upper%20center%20creating%20a%20quiet%20reading%20atmosphere%2C%20editorial%20minimalism%20with%20fine%20grain%20texture%20and%20delicate%20paper%20surface%20details%2C%20serene%20contemplative%20mood%20suitable%20for%20a%20book%20detail%20page%2C%20no%20people%2C%20no%20text%2C%20painterly%20modern%20digital%20art%20with%20understated%20elegance%2C%20publishing%20house%20brand%20visual%20identity%2C%20dark%20navy%20and%20warm%20neutral%20palette%2C%20generous%20breathing%20negative%20space&width=1600&height=800&seq=lelyric-book-detail-hero-bg&orientation=landscape"
              alt=""
              width={1600}
              height={800}
              className="w-full h-full object-cover"
            />
          </div>

          {/* Dark overlay for text readability */}
          <div className="absolute inset-0 bg-gradient-to-b from-foreground-950/75 via-foreground-950/60 to-foreground-950/80"></div>

          {/* Fine horizontal rules */}
          <div className="absolute top-0 left-0 right-0 h-px bg-background-50/6"></div>
          <div className="absolute bottom-0 left-0 right-0 h-px bg-background-50/6"></div>

          {/* Content */}
          <div className="relative z-10 w-full px-6 md:px-10 py-16 md:py-20">
            <div className="max-w-4xl mx-auto">
              <Breadcrumb
                items={[
                  { label: '首頁', href: '/' },
                  { label: '書籍', href: '/books' },
                  { label: book.title },
                ]}
                light
              />
              <div className="flex items-center gap-2.5 mb-6">
                {book.category && (
                  <>
                    <span className="block w-5 h-px bg-primary-400/40"></span>
                    <span className="text-[13px] text-primary-400/70 tracking-[0.15em] uppercase font-medium">{book.category}</span>
                  </>
                )}
              </div>
              <h1 className="font-heading text-[3rem] md:text-[4rem] lg:text-[3.75rem] font-bold text-background-50 leading-[1.12] tracking-tight mb-3">
                {book.title}
              </h1>
              {book.subtitle && (
                <p className="text-base md:text-lg text-background-50/45 max-w-xl leading-relaxed font-light tracking-wide mb-1">{book.subtitle}</p>
              )}
              {book.author && (
                <p className="text-base text-background-50/35">{book.author}</p>
              )}
            </div>
          </div>

          {/* Bottom gradient transition */}
          <div className="absolute bottom-0 left-0 right-0 h-20 bg-gradient-to-t from-background-50/20 to-transparent"></div>
        </section>

        {/* Book Detail Content */}
        <section className="px-6 md:px-10 py-16 md:py-24 bg-background-50">
          <div className="max-w-5xl mx-auto">
            <div className="flex flex-col md:flex-row gap-10 md:gap-16">
              {/* Book Cover */}
              <div className="md:w-[300px] flex-shrink-0">
                <div
                  className="aspect-[3/4] w-full max-w-[240px] md:max-w-full rounded-sm relative overflow-hidden"
                  role="img"
                  aria-label={`${book.title} 書封`}
                >
                  <img
                    src={book.coverImage}
                    alt={book.title}
                    width={300}
                    height={400}
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>

              {/* Book Info */}
              <div className="flex-1 min-w-0">
                {book.description && (
                  <div className="mb-10">
                    <p className="text-base md:text-lg text-foreground-600 leading-relaxed">
                      {book.description}
                    </p>
                  </div>
                )}

                {/* Purchase Links */}
                {book.purchaseLinks.length > 0 && (
                  <div className="mb-10">
                    <h2 className="text-base font-semibold text-foreground-800 mb-3.5">購書通路</h2>
                    <div className="flex flex-wrap gap-3">
                      {book.purchaseLinks.map((link, i) => (
                        <a
                          key={i}
                          href={link.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="whitespace-nowrap inline-flex items-center gap-2 px-4 py-2 rounded-full text-[15px] font-medium border border-foreground-200 text-foreground-700 hover:border-foreground-400 hover:bg-background-100 transition-all cursor-pointer"
                          aria-label={`前往 ${link.label} 購買 ${book.title}`}
                        >
                          {link.label}
                          <i className="ri-external-link-line text-[13px]"></i>
                        </a>
                      ))}
                    </div>
                  </div>
                )}

                {/* Catalog Link */}
                <a
                  href={siteConfig.catalogUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="whitespace-nowrap inline-flex items-center gap-2 text-[15px] text-primary-600 hover:text-primary-700 transition-colors cursor-pointer"
                  aria-label="前往崧博出版書籍資料庫查詢 ISBN 與出版資訊"
                >
                  查詢完整書籍資料（ISBN、出版日期等）
                  <i className="ri-external-link-line text-[13px]"></i>
                </a>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}